/**
 * Jobs-table column definitions (RM-118). Standard, type-agnostic columns plus
 * one modular "Szczegoly" column that defers to the per-type {@link JobSummary}.
 * `meta.label` drives the column-picker labels; `DEFAULT_COLUMN_VISIBILITY`
 * hides the noisier columns until the user opts in (persisted to localStorage).
 */
import type { ColumnDef, VisibilityState } from '@tanstack/react-table'
import type { Job } from '../../lib/schemas'
import { displayName, thumbnailUrl, JOB_TYPE_LABELS } from '../../lib/jobView'
import { StatusBadge } from '../StatusBadge'
import { Badge } from '../ui/badge'
import { JobSummary } from './JobSummary'

function fmtDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

// TanStack ColumnDef with a typed `meta.label` for the picker.
type JobColumn = ColumnDef<Job> & { meta?: { label: string } }

export const jobColumns: JobColumn[] = [
  {
    id: 'thumbnail',
    meta: { label: 'Podglad' },
    header: '',
    enableSorting: false,
    cell: ({ row }) => {
      const src = thumbnailUrl(row.original)
      return (
        <div className="h-10 w-16 overflow-hidden rounded bg-muted">
          {src && <img src={src} className="h-full w-full object-cover" />}
        </div>
      )
    },
  },
  {
    id: 'name',
    meta: { label: 'Nazwa' },
    header: 'Nazwa',
    accessorFn: (j) => displayName(j),
    cell: ({ row }) => (
      <span className="font-medium text-foreground">
        {displayName(row.original)}
      </span>
    ),
  },
  {
    id: 'type',
    meta: { label: 'Typ' },
    header: 'Typ',
    accessorFn: (j) => j.job_spec.type,
    cell: ({ row }) => (
      <Badge variant="outline">
        {JOB_TYPE_LABELS[row.original.job_spec.type]}
      </Badge>
    ),
  },
  {
    id: 'status',
    meta: { label: 'Status' },
    header: 'Status',
    accessorFn: (j) => j.status,
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
  {
    id: 'summary',
    meta: { label: 'Szczegoly' },
    header: 'Szczegoly',
    enableSorting: false,
    cell: ({ row }) => <JobSummary job={row.original} />,
  },
  {
    id: 'progress',
    meta: { label: 'Postep' },
    header: 'Postep',
    accessorFn: (j) => j.progress_percent ?? 0,
    cell: ({ row }) => {
      const p = row.original.progress_percent
      return p == null ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        <span className="tabular-nums">{p}%</span>
      )
    },
  },
  {
    id: 'created',
    meta: { label: 'Utworzono' },
    header: 'Utworzono',
    accessorFn: (j) => j.created_at,
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground tabular-nums">
        {fmtDate(row.original.created_at)}
      </span>
    ),
  },
  {
    id: 'id',
    meta: { label: 'ID' },
    header: 'ID',
    enableSorting: false,
    accessorFn: (j) => j.id,
    cell: ({ row }) => (
      <span className="font-mono text-xs text-muted-foreground">
        {row.original.id.slice(0, 8)}
      </span>
    ),
  },
]

/** Columns hidden by default (noisier / optional); the rest start visible. */
export const DEFAULT_COLUMN_VISIBILITY: VisibilityState = {
  thumbnail: false,
  progress: false,
  id: false,
}
